import { useEffect, useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Image, RefreshCw } from 'lucide-react';
import { useToast } from '@/lib/hooks/use-toast';
import {
  bugReportsService,
  type BugReportDetail,
  type BugReportStatus,
  type BugReportListItem,
} from '../services/bug-reports-service';
import { formatDateTime } from '@/lib/utils/format';

// ── helpers ────────────────────────────────────────────────────────

const TYPE_EMOJI: Record<string, string> = {
  BUG: '🐛',
  CRASH: '💥',
  UI: '🎨',
  DATA: '📊',
  SUGGESTION: '💡',
};

const TYPE_LABEL: Record<string, string> = {
  BUG: 'Bug',
  CRASH: 'Crash',
  UI: 'Visual',
  DATA: 'Datos',
  SUGGESTION: 'Sugerencia',
};

const STATUS_LABEL: Record<BugReportStatus, string> = {
  NEW: 'Nuevo',
  IN_PROGRESS: 'En progreso',
  RESOLVED: 'Resuelto',
  WONT_FIX: 'No se va a arreglar',
};

const STATUS_VARIANT: Record<BugReportStatus, 'info' | 'warning' | 'success' | 'secondary'> = {
  NEW: 'info',
  IN_PROGRESS: 'warning',
  RESOLVED: 'success',
  WONT_FIX: 'secondary',
};

// ── AuthImage — fetches image via axios so JWT is included ─────────

function AuthImage({ bugReportId, imageId }: { bugReportId: string; imageId: string }) {
  const [src, setSrc] = useState<string | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;

    bugReportsService
      .fetchImageBlob(bugReportId, imageId)
      .then((url) => {
        if (!cancelled) {
          objectUrl = url;
          setSrc(url);
        }
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [bugReportId, imageId]);

  if (error) {
    return (
      <div className="w-24 h-24 rounded border flex items-center justify-center bg-muted text-muted-foreground text-xs">
        Error
      </div>
    );
  }

  if (!src) {
    return <Skeleton className="w-24 h-24 rounded" />;
  }

  return (
    <a href={src} target="_blank" rel="noopener noreferrer">
      <img
        src={src}
        alt="Captura"
        className="w-24 h-24 rounded object-cover border cursor-pointer hover:opacity-80 transition-opacity"
      />
    </a>
  );
}

// ── Main dialog component ──────────────────────────────────────────

interface Props {
  reportId: string | null;
  onClose: () => void;
  onStatusChange: (updated: BugReportListItem) => void;
}

export function BugReportDetailDialog({ reportId, onClose, onStatusChange }: Props) {
  const [detail, setDetail] = useState<BugReportDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);
  const { toast } = useToast();

  const fetchDetail = useCallback(
    async (id: string) => {
      setLoading(true);
      setDetail(null);
      try {
        const data = await bugReportsService.get(id);
        setDetail(data);
      } catch {
        toast({ title: 'Error al cargar el reporte', variant: 'destructive' });
        onClose();
      } finally {
        setLoading(false);
      }
    },
    [toast, onClose]
  );

  useEffect(() => {
    if (reportId) fetchDetail(reportId);
  }, [reportId, fetchDetail]);

  const handleStatusChange = async (newStatus: BugReportStatus) => {
    if (!detail) return;
    setSavingStatus(true);
    try {
      const updated = await bugReportsService.updateStatus(detail.id, newStatus);
      setDetail(updated);
      onStatusChange(updated);
      toast({ title: 'Estado actualizado correctamente' });
    } catch {
      toast({ title: 'Error al actualizar el estado', variant: 'destructive' });
    } finally {
      setSavingStatus(false);
    }
  };

  return (
    <Dialog
      open={!!reportId}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent
        aria-describedby={undefined}
        className="max-w-2xl max-h-[85vh] overflow-y-auto"
      >
        {loading && (
          <div className="space-y-3 py-4">
            <Skeleton className="h-6 w-3/4" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        )}

        {!loading && detail && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-left pr-6">
                <span className="text-xl">{TYPE_EMOJI[detail.type] ?? '📝'}</span>
                <span className="leading-snug">{detail.title}</span>
              </DialogTitle>
            </DialogHeader>

            {/* Metadata */}
            <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm border rounded-lg p-4 bg-muted/30">
              <div>
                <span className="text-muted-foreground font-medium">ID:</span>{' '}
                <span className="font-mono text-xs">{detail.id}</span>
              </div>
              <div>
                <span className="text-muted-foreground font-medium">Fecha:</span>{' '}
                {formatDateTime(detail.createdAt)}
              </div>
              <div>
                <span className="text-muted-foreground font-medium">Tipo:</span>{' '}
                {TYPE_EMOJI[detail.type]} {TYPE_LABEL[detail.type] ?? detail.type}
              </div>
              <div className="flex items-center gap-1">
                <span className="text-muted-foreground font-medium">Estado:</span>{' '}
                <Badge variant={STATUS_VARIANT[detail.status]}>{STATUS_LABEL[detail.status]}</Badge>
              </div>
              <div>
                <span className="text-muted-foreground font-medium">Empresa:</span>{' '}
                {detail.tenant?.name ?? '—'}
              </div>
              <div>
                <span className="text-muted-foreground font-medium">Usuario:</span>{' '}
                {detail.user?.email ?? '—'}
              </div>
              {detail.url && (
                <div className="col-span-2">
                  <span className="text-muted-foreground font-medium">URL:</span>{' '}
                  <a
                    href={detail.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-500 underline break-all"
                  >
                    {detail.url}
                  </a>
                </div>
              )}
              {detail.browser && (
                <div className="col-span-2">
                  <span className="text-muted-foreground font-medium">Navegador:</span>{' '}
                  {detail.browser}
                </div>
              )}
              {detail.telegramMessageId && (
                <div className="col-span-2">
                  <span className="text-muted-foreground font-medium">Telegram msg ID:</span>{' '}
                  {detail.telegramMessageId}
                  {detail.telegramChatId && (
                    <span className="text-muted-foreground ml-2">
                      (chat: {detail.telegramChatId})
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Descripción */}
            <div>
              <h3 className="text-sm font-semibold mb-1">Descripción</h3>
              <p className="text-sm whitespace-pre-wrap text-muted-foreground">
                {detail.description}
              </p>
            </div>

            {/* Pasos / Esperado / Actual */}
            {detail.steps && (
              <div>
                <h3 className="text-sm font-semibold mb-1">Pasos para reproducir</h3>
                <p className="text-sm whitespace-pre-wrap text-muted-foreground">{detail.steps}</p>
              </div>
            )}
            {detail.expected && (
              <div>
                <h3 className="text-sm font-semibold mb-1">Comportamiento esperado</h3>
                <p className="text-sm whitespace-pre-wrap text-muted-foreground">
                  {detail.expected}
                </p>
              </div>
            )}
            {detail.actual && (
              <div>
                <h3 className="text-sm font-semibold mb-1">Comportamiento actual</h3>
                <p className="text-sm whitespace-pre-wrap text-muted-foreground">{detail.actual}</p>
              </div>
            )}

            {/* Imágenes */}
            {detail.images.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold mb-2 flex items-center gap-1">
                  <Image className="h-4 w-4" />
                  Imágenes ({detail.images.length})
                </h3>
                <div className="flex flex-wrap gap-2">
                  {detail.images
                    .sort((a, b) => a.position - b.position)
                    .map((img) => (
                      <AuthImage key={img.id} bugReportId={detail.id} imageId={img.id} />
                    ))}
                </div>
              </div>
            )}

            {/* Acciones */}
            <div className="border-t pt-4">
              <h3 className="text-sm font-semibold mb-2">Acciones</h3>
              <div className="flex items-center gap-3">
                <Select
                  value={detail.status}
                  onValueChange={(v) => handleStatusChange(v as BugReportStatus)}
                  disabled={savingStatus}
                >
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Cambiar estado" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="NEW">Nuevo</SelectItem>
                    <SelectItem value="IN_PROGRESS">En progreso</SelectItem>
                    <SelectItem value="RESOLVED">Resuelto</SelectItem>
                    <SelectItem value="WONT_FIX">No se va a arreglar</SelectItem>
                  </SelectContent>
                </Select>
                {savingStatus && (
                  <RefreshCw className="h-4 w-4 animate-spin text-muted-foreground" />
                )}
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
